import { useEffect, useRef, useState } from "react";
import { mayRetainSetupBundle } from "./DeviceProvisioningPanel";

type Api = <T>(
  path: string,
  options?: {
    method?: string;
    body?: unknown;
    csrf?: string | undefined;
    signal?: AbortSignal;
  },
) => Promise<T>;
export type VoiceSetup = {
  enrollmentId: string | null;
  assignmentHash: string;
  assignments: {
    port: number;
    lineId: string;
    label: string;
    extension: string | null;
    numbers: string[];
  }[];
  canPrepare: boolean;
  issue: string;
  callingReady: false;
  preparedAssignmentsMatch: boolean;
  publishedAssignmentsMatch: boolean;
  profile: {
    latestRevision: number;
    activeRevision: number | null;
    sipPublished: boolean;
    state: string;
  } | null;
};
export function VoiceSetupAssignments({ value }: { value: VoiceSetup }) {
  return (
    <>
      {!value.assignments.length ? (
        <p>
          No port assignment yet. Use Assign number and equipment to connect
          this device to a line.
        </p>
      ) : (
        <dl className="cr-details">
          {value.assignments.map((item) => (
            <div key={`${item.port}:${item.lineId}`}>
              <dt>Phone port {item.port}</dt>
              <dd>
                {item.label}
                {item.extension ? ` · Extension ${item.extension}` : ""}
                <br />
                {item.numbers.length
                  ? item.numbers.join(", ")
                  : "No incoming number assigned"}
              </dd>
            </div>
          ))}
        </dl>
      )}
      <p className="cr-form-note">
        These are saved assignments. Preparing settings creates this device's
        administrator password and a separate phone password for each assigned
        port. Unassigned ports stay disabled.
      </p>
      {value.profile && value.profile.latestRevision > 0 && (
        <p role="status">
          Prepared revision {value.profile.latestRevision}.
          {!value.preparedAssignmentsMatch &&
            " Assignments have changed since these settings were prepared. Prepare updated settings for the assignments above."}
          {value.profile.sipPublished
            ? ` Revision ${value.profile.activeRevision} is available for download${value.publishedAssignmentsMatch ? "" : " for earlier assignments"}. Download availability alone does not confirm installation or calling; check the separate registration and calling records.`
            : " Phone settings have not been released to this device."}
        </p>
      )}
      {value.issue && <p className="cr-form-note">{value.issue}</p>}
    </>
  );
}
export function DeviceVoiceProfilePanel(props: {
  api: Api;
  path: string;
  csrf?: string | undefined;
}) {
  const [status, setStatus] = useState<VoiceSetup | null>(null);
  const [refresh, setRefresh] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [secret, setSecret] = useState<{
    adminPassword: string;
    revision: number;
  } | null>(null);
  const generation = useRef(0),
    secretGeneration = useRef(0);
  const path = `${props.path}/voice-profile`;
  useEffect(() => {
    const hide = () => {
      ++secretGeneration.current;
      setSecret(null);
    };
    const visibility = () => {
      if (document.visibilityState !== "visible") hide();
    };
    window.addEventListener("blur", hide);
    window.addEventListener("pagehide", hide);
    document.addEventListener("visibilitychange", visibility);
    return () => {
      hide();
      window.removeEventListener("blur", hide);
      window.removeEventListener("pagehide", hide);
      document.removeEventListener("visibilitychange", visibility);
    };
  }, []);
  useEffect(() => {
    const current = ++generation.current;
    ++secretGeneration.current;
    const controller = new AbortController();
    setSecret(null);
    setStatus(null);
    setBusy(true);
    setError("");
    setNotice("");
    setConfirmed(false);
    props
      .api<VoiceSetup>(path, { signal: controller.signal })
      .then((value) => {
        if (generation.current === current) setStatus(value);
      })
      .catch((reason) => {
        if (!controller.signal.aborted && generation.current === current)
          setError(
            reason instanceof Error
              ? reason.message
              : "Phone settings could not be loaded. Refresh to retry.",
          );
      })
      .finally(() => {
        if (generation.current === current) setBusy(false);
      });
    return () => {
      ++generation.current;
      ++secretGeneration.current;
      controller.abort();
    };
  }, [props.api, path, refresh]);
  useEffect(() => {
    if (!secret) return;
    const timeout = setTimeout(() => {
      ++secretGeneration.current;
      setSecret(null);
    }, 60_000);
    return () => clearTimeout(timeout);
  }, [secret]);
  async function prepare() {
    if (!status?.enrollmentId || !status.canPrepare || !confirmed || busy)
      return;
    const current = generation.current;
    setBusy(true);
    setError("");
    setNotice("");
    setSecret(null);
    ++secretGeneration.current;
    try {
      const value = await props.api<VoiceSetup>(`${path}/prepare`, {
        method: "POST",
        csrf: props.csrf,
        body: {
          enrollmentId: status.enrollmentId,
          expectedRevision: status.profile?.latestRevision ?? 0,
          assignmentHash: status.assignmentHash,
          confirmed: true,
        },
      });
      if (generation.current !== current) return;
      setStatus(value);
      setConfirmed(false);
      setNotice(
        "Phone settings prepared. Activation still needs the device connection, emergency-service checks and an applied phone-system configuration.",
      );
    } catch (reason) {
      if (generation.current === current) {
        setConfirmed(false);
        setError(
          `${reason instanceof Error ? reason.message : "Preparation could not be confirmed."} Refresh before retrying; preparation may have completed.`,
        );
      }
    } finally {
      if (generation.current === current) setBusy(false);
    }
  }
  async function reveal(selectedRevision: number) {
    if (!status?.enrollmentId || !status.profile || busy) return;
    const current = generation.current,
      secretRequest = ++secretGeneration.current;
    setBusy(true);
    setSecret(null);
    setError("");
    setNotice("");
    try {
      const value = await props.api<{
        adminPassword: string;
        revision: number;
      }>(`${path}/admin-password`, {
        method: "POST",
        csrf: props.csrf,
        body: {
          enrollmentId: status.enrollmentId,
          expectedRevision: status.profile.latestRevision,
          revision: selectedRevision,
          confirmed: true,
        },
      });
      if (generation.current !== current) return;
      if (
        mayRetainSetupBundle(
          secretRequest,
          secretGeneration.current,
          document.visibilityState === "visible",
          document.hasFocus(),
        )
      )
        setSecret({
          adminPassword: value.adminPassword,
          revision: value.revision,
        });
      else
        setNotice(
          "The window lost focus. The password was discarded; reveal it again when needed.",
        );
    } catch (reason) {
      if (generation.current === current)
        setError(
          reason instanceof Error
            ? reason.message
            : "The password could not be retrieved. Refresh to retry.",
        );
    } finally {
      if (generation.current === current) setBusy(false);
    }
  }
  return (
    <section
      className="cr-device-voice-profile"
      aria-label="Phone configuration"
    >
      <h4>Phone configuration</h4>
      {busy && <p role="status">Loading phone settings…</p>}
      {error && (
        <p className="cr-notice error" role="alert">
          {error}
        </p>
      )}
      {notice && <p role="status">{notice}</p>}
      {status && <VoiceSetupAssignments value={status} />}
      <button
        type="button"
        className="cr-button secondary"
        disabled={busy}
        onClick={() => setRefresh((value) => value + 1)}
      >
        Refresh phone settings
      </button>
      {status?.canPrepare && (
        <div>
          <label className="cr-check">
            <input
              type="checkbox"
              checked={confirmed}
              onChange={(event) => setConfirmed(event.target.checked)}
            />
            I checked the line and port assignments above.
          </label>
          <button
            type="button"
            className="cr-button"
            disabled={busy || !confirmed}
            onClick={prepare}
          >
            Prepare phone settings
          </button>
        </div>
      )}
      {status?.profile && status.profile.latestRevision > 0 && (
        <div>
          <h4>Device administrator password</h4>
          <p>
            Staff access is recorded. This password belongs to a configuration
            revision, not a confirmed installation. It hides after one minute or
            when you leave this window.
          </p>
          <button
            type="button"
            className="cr-button secondary"
            disabled={busy}
            onClick={() => reveal(status.profile!.latestRevision)}
          >
            Show prepared password (revision {status.profile.latestRevision})
          </button>
          {status.profile.activeRevision &&
            status.profile.activeRevision !== status.profile.latestRevision && (
              <button
                type="button"
                className="cr-button secondary"
                disabled={busy}
                onClick={() => reveal(status.profile!.activeRevision!)}
              >
                Show published password (revision{" "}
                {status.profile.activeRevision})
              </button>
            )}
        </div>
      )}
      {secret && (
        <div>
          <label className="cr-field">
            <span>Administrator password, revision {secret.revision}</span>
            <input
              readOnly
              autoComplete="off"
              value={secret.adminPassword}
              onFocus={(event) => event.currentTarget.select()}
            />
          </label>
          <button
            type="button"
            className="cr-button secondary"
            onClick={() => {
              ++secretGeneration.current;
              setSecret(null);
            }}
          >
            Hide password
          </button>
        </div>
      )}
    </section>
  );
}
